/**
 * External Imports
 */
import { FC, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { pdf } from '@react-pdf/renderer';
/**
 * Internal Imports
 */
import ComplianceHeader from '../../../components/searchHeader/complianceHeader/ComplianceHeader';
import ComplianceReportTable from '../../../components/customTables/reports/patient/complianceReportTable';
import { RootState } from '../../../redux/store';
import {
  clearComplianceReportFilter,
  getCSVToExportSuccess,
  getComplianceReport
} from '../../../redux/actions';
import PatientCompliancePdfTableFile from '../../../components/shared/buttons/DownloadPdfBtn/PdfTable/patient/PatientCompliancePdfTableFile';
import {
  calculateTotalCompliance,
  reduceComplianceByUnit
} from '../../../utils/complianceFunctions';
import { date as dt } from '../../../utils/helpers';
/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  ComplianceObj,
  ComplianceReport as IComplianceReport,
  ReducedUnitComp,
  Unit,
  Filter
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { CReportContainer } from '../styles/ReportsViews.styles';

interface ComplianceReportProps {
  complianceReport: ComplianceObj[];
  authorizedUser: AuthUserProps;
  units: Unit[];
  selectedUnits: { id: string; unit: string }[];
  complianceReportFilter: Filter;
}

const ComplianceReport: FC<ComplianceReportProps> = ({
  complianceReport = [],
  authorizedUser,
  units = [],
  selectedUnits,
  complianceReportFilter
}) => {
  const dispatch = useDispatch();
  const [csvData, setCsvData] = useState<IComplianceReport[]>([]);
  const [listToDisplay, setListToDisplay] = useState<IComplianceReport[]>([]);
  const [listToDisplayTotal, setListToDisplayTotal] =
    useState<IComplianceReport | null>(null);

  useEffect(() => {
    return () => {
      dispatch(clearComplianceReportFilter());
    };
  }, []);

  useEffect(() => {
    const today = new Date();
    const todayDate = format(
      new Date(today.getFullYear(), today.getMonth(), today.getDate()),
      'yyyy-MM-dd'
    );
    // when dates change
    dispatch(
      getComplianceReport({
        facilityId: authorizedUser?.facilityId,
        startday:
          complianceReportFilter.startDate?.replace(/-/g, '') ??
          todayDate.replace(/-/g, ''),
        endday: dt
          .addDayToDayOfYearDate(complianceReportFilter.endDate ?? todayDate)
          .replace(/-/g, '')
      })
    );
  }, [authorizedUser?.facilityId, complianceReportFilter]);

  useEffect(() => {
    if (units.length > 0) {
      const reducedCompliance = reduceComplianceByUnit(complianceReport, units);
      const complianceValues: ReducedUnitComp[] =
        Object.values(reducedCompliance);
      const complianceByUnitFilter = complianceValues.filter(
        (comp: ReducedUnitComp) =>
          selectedUnits.some(unit => unit.id === comp.groupId)
      );

      const cleanCompliance = complianceByUnitFilter.map(
        (item: ReducedUnitComp) => {
          return {
            unit: item.groupName,
            unitId: item.groupId,
            unitType: item.locTypeDisplay,
            census: item.combinedData.census,
            roundedPercent: item.combinedData.roundedPct,
            roundedNumber: item.combinedData.roundedTotal, // Future: possibly change to just rounded Yes
            unablePercent: item.combinedData.unablePct,
            unableNumber: item.combinedData.unable,
            notRoundedPercent: item.combinedData.nonePct,
            notRoundedNumber: item.combinedData.none
          };
        }
      );
      setListToDisplay(cleanCompliance);
      if (selectedUnits.length > 0) {
        setListToDisplayTotal(
          calculateTotalCompliance(
            cleanCompliance,
            selectedUnits.length === units.length
              ? 'Facility Total'
              : 'Selected Units Total',
            selectedUnits.length === units.length
              ? 'FACILITY'
              : selectedUnits.map(u => u.id).join(',')
          )
        );
      } else {
        setListToDisplayTotal(null);
      }
      setCsvData(cleanCompliance);
    }
  }, [complianceReport, selectedUnits, units]);

  const handleExportCsv = () => {
    dispatch(getCSVToExportSuccess(csvData));
  };

  const handleClickDownloadPDF = async () => {
    const asPdf = pdf(<PatientCompliancePdfTableFile data={csvData} />);

    const blob = await asPdf.toBlob();
    function downloadBlob(name = 'file.txt') {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = name;

      document.body.appendChild(link);
      link.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        })
      );
      document.body.removeChild(link);
    }

    return downloadBlob(
      `patient-compliance-report-${new Date().toISOString()}.pdf`
    );
  };

  return (
    <CReportContainer id="Compliance-Report-Container">
      <ComplianceHeader
        handleExportCsv={handleExportCsv}
        handleClickDownloadPDF={handleClickDownloadPDF}
      />
      <ComplianceReportTable
        complianceList={listToDisplay}
        complianceTotal={listToDisplayTotal}
      />
    </CReportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    complianceReport: state.ReportReducer.complianceReport,
    selectedUnits: state.ConfigReducer.selectedUnits,
    complianceReportFilter: state.ReportReducer.complianceReportFilter,
    units: state.ConfigReducer.units
  };
};

export default connect(mapReduxStateToProps)(ComplianceReport);
